import { format } from 'date-fns'
import { fr } from 'date-fns/locale'

import { getDocumentTypeLabel } from './medical-documents'
import type { MedicalDocument } from '@/types/health'

export interface MedicalDocumentAnalysisState {
  confidenceLabel: 'high' | 'medium' | 'low' | null
  confidenceScore: number | null
  reviewRequired: boolean
  missingFields: string[]
}

export function normalizeMedicalDocumentText(value: string | null | undefined): string {
  return (value || '').replace(/\s+/g, ' ').trim()
}

export function truncateMedicalDocumentText(value: string, maxLength = 120): string {
  if (value.length <= maxLength) {
    return value
  }

  return `${value.slice(0, maxLength).trimEnd()}...`
}

export function getMedicalDocumentExtractedText(
  document: MedicalDocument,
  key: string
): string | null {
  const extractedData = document.extracted_data as Record<string, unknown> | null
  const value = extractedData?.[key]
  return typeof value === 'string' ? normalizeMedicalDocumentText(value) || null : null
}

export function getMedicalDocumentManualAnnotation(document: MedicalDocument): string | null {
  return getMedicalDocumentExtractedText(document, 'manual_annotation')
}

export function getMedicalDocumentManualAnnotationUpdatedAt(document: MedicalDocument): string | null {
  return getMedicalDocumentExtractedText(document, 'manual_annotation_updated_at')
}

export function inferMedicalDocumentSpecialty(document: MedicalDocument): string {
  const explicitSpecialty = [
    normalizeMedicalDocumentText(document.specialty),
    getMedicalDocumentExtractedText(document, 'inferred_specialty'),
    getMedicalDocumentExtractedText(document, 'specialty'),
    getMedicalDocumentExtractedText(document, 'specialite')
  ].find(Boolean)

  if (explicitSpecialty) {
    return explicitSpecialty
  }

  switch (document.document_type) {
    case 'blood_test':
      return 'Biologie'
    case 'radiology':
      return 'Imagerie'
    case 'prescription':
      return 'Prescription'
    case 'consultation_report':
    case 'specialist_report':
      return 'Consultation'
    case 'cardiology':
      return 'Cardiologie'
    case 'pathology':
      return 'Anatomopathologie'
    case 'vaccination_record':
      return 'Vaccination'
    case 'insurance_document':
      return 'Administratif'
    default:
      return 'Autre'
  }
}

function isDateLike(value: string | null | undefined): value is string {
  return Boolean(value && /^\d{4}-\d{2}-\d{2}/.test(value))
}

export function getMedicalDocumentClinicalDate(document: MedicalDocument): {
  label: string
  rawDate: string
} {
  const prelevementDate = getMedicalDocumentExtractedText(document, 'date_prelevement')

  if (isDateLike(prelevementDate)) {
    return {
      label: document.document_type === 'blood_test' ? 'Prelevement' : 'Examen',
      rawDate: prelevementDate
    }
  }

  const extractedDocumentDate = getMedicalDocumentExtractedText(document, 'date_document')
  if (isDateLike(extractedDocumentDate)) {
    return {
      label: 'Document',
      rawDate: extractedDocumentDate
    }
  }

  return {
    label: 'Document',
    rawDate: document.document_date
  }
}

export function formatMedicalDocumentDate(rawDate: string): string {
  if (!isDateLike(rawDate)) {
    return rawDate
  }

  return format(new Date(rawDate), 'd MMM yyyy', { locale: fr })
}

export function getMedicalDocumentDisplayTitle(document: MedicalDocument): string {
  return getDocumentTypeLabel(document.document_type)
}

export function getMedicalDocumentDoctor(document: MedicalDocument): string | null {
  const doctor = [
    normalizeMedicalDocumentText(document.practitioner_name),
    getMedicalDocumentExtractedText(document, 'medecin'),
    getMedicalDocumentExtractedText(document, 'doctor_name')
  ].find(Boolean)

  if (!doctor) {
    return null
  }

  return /^dr\b/i.test(doctor) ? doctor : `Dr. ${doctor}`
}

export function getMedicalDocumentPrimaryFacts(document: MedicalDocument): string[] {
  const clinicalDate = getMedicalDocumentClinicalDate(document)
  const doctor = getMedicalDocumentDoctor(document)
  const facts = [
    getMedicalDocumentDisplayTitle(document),
    `${clinicalDate.label} ${formatMedicalDocumentDate(clinicalDate.rawDate)}`
  ]

  if (doctor) {
    facts.push(doctor)
  } else if (document.medical_facility) {
    facts.push(normalizeMedicalDocumentText(document.medical_facility))
  }

  return facts
}

export function getMedicalDocumentSummary(document: MedicalDocument): string {
  const extractedData = document.extracted_data as Record<string, unknown> | null
  const summaryCandidates = [
    getMedicalDocumentExtractedText(document, 'summary_short'),
    normalizeMedicalDocumentText(document.description),
    getMedicalDocumentExtractedText(document, 'resume'),
    getMedicalDocumentExtractedText(document, 'summary'),
    getMedicalDocumentExtractedText(document, 'recommandations')
  ].filter(Boolean)

  if (summaryCandidates.length > 0) {
    return truncateMedicalDocumentText(summaryCandidates[0], 135)
  }

  const analysesCount = Array.isArray(extractedData?.analyses) ? extractedData.analyses.length : 0
  if (analysesCount > 0) {
    return `${analysesCount} mesure${analysesCount > 1 ? 's' : ''} biologique${analysesCount > 1 ? 's' : ''} extraite${analysesCount > 1 ? 's' : ''}`
  }

  const diagnosticsCount = Array.isArray(extractedData?.diagnostics) ? extractedData.diagnostics.length : 0
  if (diagnosticsCount > 0) {
    return `${diagnosticsCount} diagnostic${diagnosticsCount > 1 ? 's' : ''} repere${diagnosticsCount > 1 ? 's' : ''}`
  }

  const prescriptionsCount = Array.isArray(extractedData?.prescriptions) ? extractedData.prescriptions.length : 0
  if (prescriptionsCount > 0) {
    return `${prescriptionsCount} prescription${prescriptionsCount > 1 ? 's' : ''} detectee${prescriptionsCount > 1 ? 's' : ''}`
  }

  return 'Document medical importe'
}

export function getMedicalDocumentTimelineSummary(document: MedicalDocument): string {
  const extractedTimelineSummary = getMedicalDocumentExtractedText(document, 'timeline_summary')

  if (extractedTimelineSummary) {
    return extractedTimelineSummary
  }

  return getMedicalDocumentSummary(document)
}

export function getMedicalDocumentAnalysisState(document: MedicalDocument): MedicalDocumentAnalysisState {
  const extractedData = document.extracted_data as Record<string, unknown> | null
  const confidenceLabel =
    extractedData?.analysis_confidence_label === 'high' ||
    extractedData?.analysis_confidence_label === 'medium' ||
    extractedData?.analysis_confidence_label === 'low'
      ? extractedData.analysis_confidence_label
      : null
  const confidenceScore =
    typeof extractedData?.analysis_confidence_score === 'number'
      ? extractedData.analysis_confidence_score
      : null
  const reviewRequired = extractedData?.analysis_review_required === true
  const missingFields = Array.isArray(extractedData?.analysis_missing_fields)
    ? extractedData.analysis_missing_fields.filter((value): value is string => typeof value === 'string')
    : []

  return {
    confidenceLabel,
    confidenceScore,
    reviewRequired,
    missingFields
  }
}

export function buildMedicalDocumentPreviewSubtitle(document: MedicalDocument): string {
  return getMedicalDocumentPrimaryFacts(document).join(' - ')
}
